/**
 * Decrypt legacy embedded wallet blobs (v1 JSON: salt/iv/ciphertext) that used
 * PBKDF2 with 100_000 iterations — kept only for one-way migration to crypto.ts (250k).
 */

const LEGACY_PBKDF2_ITERATIONS = 100_000

type EmbeddedWalletBlobV1 = {
  v: 1
  saltB64: string
  ivB64: string
  ciphertextB64: string
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

async function deriveLegacyAesKeyFromPassword(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const pwKey = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: new Uint8Array(salt),
      iterations: LEGACY_PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    pwKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/**
 * Decrypt a legacy v1 JSON blob (100k PBKDF2). Used only during migration.
 */
export async function decryptLegacyEmbeddedBlobV1(rawJson: string, password: string): Promise<string> {
  let blob: EmbeddedWalletBlobV1
  try {
    blob = JSON.parse(rawJson) as EmbeddedWalletBlobV1
  } catch {
    throw new Error('Invalid embedded wallet storage format')
  }
  if (!blob || blob.v !== 1) throw new Error('Unsupported embedded wallet format')

  const aesKey = await deriveLegacyAesKeyFromPassword(password, base64ToBytes(blob.saltB64))

  let plaintext: ArrayBuffer
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(base64ToBytes(blob.ivB64)) },
      aesKey,
      new Uint8Array(base64ToBytes(blob.ciphertextB64)),
    )
  } catch {
    throw new Error('Wrong password')
  }

  return new TextDecoder().decode(plaintext)
}
